import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import AppLayout from "@/components/layout/AppLayout";
import RequireCap, { payments as canPay } from "@/components/RequireCap";
import { can } from "@/lib/permissions";
import "@/App.css";

// Pages (lazy-loaded for performance)
import LandingPage    from "@/pages/LandingPage";
import NotFoundPage   from "@/pages/NotFoundPage";
import AuthPage       from "@/pages/AuthPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage  from "@/pages/DashboardPage";
import ProjectListPage from "@/pages/ProjectListPage";
import ProjectDashboardPage from "@/pages/ProjectDashboardPage";
import BankReportPage from "@/pages/BankReportPage";
import BillAuditPage from "@/pages/BillAuditPage";
import FinancialDocPage from "@/pages/FinancialDocPage";
import DocumentVaultPage from "@/pages/DocumentVaultPage";
import DPRPage        from "@/pages/DPRPage";
import MRNPage        from "@/pages/MRNPage";
import ClientPortalPage from "@/pages/ClientPortalPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import SettingsPage   from "@/pages/SettingsPage";
import TeamPage       from "@/pages/TeamPage";
import BillingPage    from "@/pages/BillingPage";
import NotificationsPage from "@/pages/NotificationsPage";
import ReportsPage    from "@/pages/ReportsPage";
import MastersPage    from "@/pages/MastersPage";
import PaymentsHubPage from "@/pages/PaymentsHubPage";
import SolarDesignPage from "@/pages/SolarDesignPage";
import CRMPipelinePage from "@/pages/CRMPipelinePage";
import CRMReportsPage from "@/pages/CRMReportsPage";
import OAAssessmentPage from "@/pages/OAAssessmentPage";
import PPAOfferPage from "@/pages/PPAOfferPage";
import PulsePage from "@/pages/PulsePage";
import MyTasksPage from "@/pages/MyTasksPage";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import { RouteSeo } from "@/lib/seo";
import SolarPMSoftwarePage from "@/pages/SolarPMSoftwarePage";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteSeo />
        <RouteErrorBoundary>
        <Routes>
          {/* Public */}
          <Route path="/"        element={<LandingPage />} />
          <Route path="/solar-project-management-software-india" element={<SolarPMSoftwarePage />} />
          <Route path="/login"   element={<AuthPage mode="login" />} />
          <Route path="/register" element={<AuthPage mode="register" />} />
          <Route path="/onboarding" element={<OnboardingPage />} />

          {/* Client portal */}
          <Route path="/client/portal/:shareToken" element={<ClientPortalPage />} />

          {/* Super Admin */}
          <Route path="/superadmin" element={<SuperAdminPage />} />

          {/* Public tools */}
          <Route path="/OA-Assessment-MH" element={<OAAssessmentPage />} />
          <Route path="/tools/ppa-offer-generator" element={<PPAOfferPage />} />

          {/* Protected App */}
          <Route path="/app" element={<AppLayout />}>
            <Route index element={<Navigate to="/app/dashboard" replace />} />
            <Route path="dashboard"  element={<DashboardPage />} />
            <Route path="projects"   element={<ProjectListPage />} />
            <Route path="projects/:id" element={<ProjectDashboardPage />} />
            <Route path="projects/:id/bank-report" element={<BankReportPage />} />
            <Route path="crm/leads/:leadId/report" element={<BankReportPage />} />
            <Route path="bill-audit"               element={<RequireCap cap="page.bill_audit"><BillAuditPage /></RequireCap>} />
            <Route path="bill-audit/:cid"          element={<RequireCap cap="page.bill_audit"><BillAuditPage /></RequireCap>} />
            <Route path="crm/leads/:leadId/bill-audit"      element={<RequireCap cap="page.bill_audit"><BillAuditPage /></RequireCap>} />
            <Route path="crm/leads/:leadId/bill-audit/:cid" element={<RequireCap cap="page.bill_audit"><BillAuditPage /></RequireCap>} />
            <Route path="projects/:id/bill-audit"           element={<RequireCap cap="page.bill_audit"><BillAuditPage /></RequireCap>} />
            <Route path="projects/:id/bill-audit/:cid"      element={<RequireCap cap="page.bill_audit"><BillAuditPage /></RequireCap>} />
            <Route path="projects/:id/financials/po"  element={<RequireCap cap="po.view"><FinancialDocPage type="po" /></RequireCap>} />
            <Route path="projects/:id/financials/pi"  element={<RequireCap cap={(u) => u.role === "client" || can(u, "pi.view")}><FinancialDocPage type="pi" /></RequireCap>} />
            <Route path="projects/:id/documents"       element={<DocumentVaultPage />} />
            <Route path="projects/:id/dpr"             element={<DPRPage />} />
            <Route path="projects/:id/mrn"             element={<RequireCap cap="mrn.view"><MRNPage /></RequireCap>} />
            <Route path="reports"        element={<RequireCap cap="reports.view"><ReportsPage /></RequireCap>} />
            <Route path="masters"        element={<RequireCap cap="page.masters"><MastersPage /></RequireCap>} />
            <Route path="payments"       element={<RequireCap cap={canPay}><PaymentsHubPage /></RequireCap>} />
            <Route path="team"           element={<TeamPage />} />
            <Route path="settings"       element={<SettingsPage />} />
            <Route path="billing"        element={<RequireCap cap="billing.manage"><BillingPage /></RequireCap>} />
            <Route path="notifications"  element={<NotificationsPage />} />
            <Route path="solar-design"   element={<SolarDesignPage />} />
            <Route path="projects/:id/solar-design" element={<SolarDesignPage />} />
            <Route path="crm"            element={<RequireCap cap="crm.view"><CRMPipelinePage /></RequireCap>} />
            <Route path="crm-reports"    element={<RequireCap cap="crm.view"><CRMReportsPage /></RequireCap>} />
            <Route path="pulse"          element={<PulsePage />} />
            <Route path="my-tasks"       element={<MyTasksPage />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </RouteErrorBoundary>
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
